import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Select, Radio, Typography, theme, App } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCategories } from '@/hooks';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { AffectedVendor } from '@/types/api/category';

const { Text } = Typography;

interface ReplaceCategoryModalProps {
  visible: boolean;
  onClose: () => void;
  onReplace: (replacementCategoryId: number | null) => Promise<void>;
  categoryName: string;
  categoryId: number;
  affectedVendors: AffectedVendor[];
  /** Customer records could not be checked (customers backend unavailable, D-CAT-17) */
  customersUnavailable?: boolean;
}

type ReplaceOption = 'replace' | 'notNow';

const ReplaceCategoryModal = ({
  visible,
  onClose,
  onReplace,
  categoryName,
  categoryId,
  affectedVendors,
  customersUnavailable = false,
}: ReplaceCategoryModalProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation(['categories', 'common']);
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const [selectedOption, setSelectedOption] = useState<ReplaceOption>('replace');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch categories for replacement dropdown (activeOnly to get all active categories)
  const { categories } = useCategories({ activeOnly: true });

  // Category options with color bar indicators (following CategorySelectWithCreate pattern)
  const categoryOptions = useMemo(() => {
    // Filter out the category being deactivated and inactive categories
    const availableCategories = categories?.filter(
      (cat) => cat.categoryId !== categoryId && cat.isActive
    ) || [];
    return availableCategories.map((cat) => ({
      label: (
        <div className="tw-flex tw-items-center tw-gap-2">
          <div
            style={{
              width: '3px',
              height: token.sizeSM,
              backgroundColor: cat.color || token.colorPrimary,
              borderRadius: token.borderRadiusXS,
            }}
          />
          <span>{cat.category}</span>
        </div>
      ),
      value: cat.categoryId,
    }));
  }, [categories, categoryId, token.colorPrimary, token.sizeSM, token.borderRadiusXS]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedOption('replace');
      setSelectedCategoryId(null);
    }
  }, [visible]);

  const handleOptionChange = (e: RadioChangeEvent) => {
    setSelectedOption(e.target.value);
  };

  const handleReplace = async () => {
    if (selectedOption === 'replace' && !selectedCategoryId) {
      message.warning(t('categories:category.replace.selectCategory'));
      return;
    }

    setLoading(true);
    try {
      await onReplace(selectedOption === 'replace' ? selectedCategoryId : null);
    } catch (error) {
      console.error('Failed to replace category:', error);
      message.error(t('categories:category.replace.replaceFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Global styles for dropdown items to match input height */}
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
      open={visible}
      onCancel={onClose}
      title={null}
      onOk={handleReplace}
      okText={selectedOption === 'replace' ? t('categories:category.replace.replace') : t('categories:category.replace.deactivate')}
      cancelText={t('common:actions.cancel')}
      okButtonProps={{
        style: { height: `${controlHeightTokens.controlHeightXL}px` },
        loading,
      }}
      cancelButtonProps={{
        style: { height: `${controlHeightTokens.controlHeightXL}px` },
      }}
      width={500}
    >
      {/* Question */}
      <Text
        style={{
          display: 'block',
          fontSize: token.fontSize,
          marginBottom: token.marginMD,
        }}
      >
        {t('categories:category.replace.questionPrefix')}<Text strong>[{categoryName}]</Text>{t('categories:category.replace.questionSuffix')}
      </Text>

      {/* Degraded customers leg — the list below may be incomplete (D-CAT-17) */}
      {customersUnavailable && (
        <Text
          style={{
            display: 'block',
            fontSize: token.fontSize,
            marginBottom: token.marginMD,
            color: token.colorWarning,
          }}
        >
          {t('categories:category.deactivate.customersUnavailable')}
        </Text>
      )}

      {/* Affected Vendors List */}
      <div
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
          marginBottom: token.marginLG,
          paddingLeft: token.paddingLG,
        }}
      >
        {affectedVendors.map((company) => (
          <div
            key={`${company.type}-${company.id}`}
            style={{
              padding: `${token.paddingXS}px 0`,
            }}
          >
            <Text
              style={{
                color: token.colorPrimary,
                fontWeight: 500,
                marginRight: token.marginXS,
              }}
            >
              {t(`categories:category.types.${company.type}`)}:
            </Text>
            <Text style={{ color: token.colorTextSecondary }}>
              [{company.companyId}],
            </Text>
            <Text strong style={{ marginLeft: token.marginXS }}>
              {company.name}
            </Text>
          </div>
        ))}
      </div>

      {/* Replace Options */}
      <Radio.Group
        value={selectedOption}
        onChange={handleOptionChange}
        style={{ width: '100%' }}
      >
        <div style={{ marginBottom: token.marginMD }}>
          <Radio value="replace">
            <Text strong>{t('categories:category.replace.pleaseReplaceAll')}</Text>
          </Radio>
          {selectedOption === 'replace' && (
            <div style={{ marginLeft: 24, marginTop: token.marginSM }}>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: token.marginXS }}
              >
                {t('categories:category.replace.replaceAllDescriptionPrefix')}<Text type="secondary" strong>[{categoryName}]</Text>{t('categories:category.replace.replaceAllDescriptionSuffix')}
              </Text>
              <Select
                size="large"
                showSearch
                placeholder={t('categories:category.replace.selectCategory')}
                value={selectedCategoryId}
                onChange={(value) => setSelectedCategoryId(value)}
                style={formStyles.inputStyle}
                options={categoryOptions}
                filterOption={(input, option) => {
                  // Search by category name text
                  const label = option?.label;
                  if (typeof label === 'object' && label !== null && 'props' in label) {
                    const children = (label as React.ReactElement).props.children;
                    if (Array.isArray(children)) {
                      const textSpan = children.find((child: React.ReactNode) =>
                        typeof child === 'object' && child !== null && 'type' in child && (child as React.ReactElement).type === 'span'
                      );
                      if (textSpan && typeof textSpan === 'object' && 'props' in textSpan) {
                        const text = (textSpan as React.ReactElement).props.children;
                        return String(text).toLowerCase().includes(input.toLowerCase());
                      }
                    }
                  }
                  return false;
                }}
              />
            </div>
          )}
        </div>

        <div>
          <Radio value="notNow">
            <Text>{t('categories:category.replace.notNow')}</Text>
          </Radio>
        </div>
      </Radio.Group>
    </Modal>
    </>
  );
};

export default ReplaceCategoryModal;
